import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { makeStyles } from "@mui/styles";
import {
  Avatar,
  Box,
  Button,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SquarehubLoader from "bundles/common/components/SquarehubLoader";
import sh from "bundles/common/utils/sh";
import { useSnackbar } from "notistack";

const useStyles = makeStyles((theme) => ({
  modalContent: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: theme.palette.common.white,
    borderRadius: "16px",
    width: "min(520px, calc(100vw - 32px))",
    maxHeight: "min(560px, calc(100vh - 32px))",
    outline: "none",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 20px 0",
  },
  closeBtn: {
    cursor: "pointer",
    color: theme.palette.text?.secondary || "#666",
    "&:hover": { color: theme.palette.text?.primary || "#111" },
  },
  body: {
    padding: "20px",
    overflowY: "auto",
    flex: 1,
  },
  footer: {
    padding: "14px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderTop: `1px solid ${theme.palette.grey?.[200] || "#EEE"}`,
  },
  sendBtn: {
    borderRadius: "999px",
    padding: "10px 28px",
    fontWeight: 800,
    textTransform: "none",
    background: theme.palette.primary?.dark || theme.palette.text?.main || "#0F172A",
    color: theme.palette.common.white,
    "&:hover": {
      background: theme.palette.secondary.hover,
      color: theme.palette.text.main,
    },
    "&:disabled": {
      background: theme.palette.grey?.[300] || "#DDD",
      color: theme.palette.grey?.[500] || "#999",
    },
  },
  cancelBtn: {
    textTransform: "none",
    fontWeight: 700,
    color: theme.palette.text?.secondary || "#666",
    borderRadius: "999px",
    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
  },
}));

const ContactCandidateModal = ({ open, onClose, candidate }) => {
  const classes = useStyles();
  const intl = useIntl();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const i18nLocale = useSelector((state) => state.i18nLocale);

  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [existingConversation, setExistingConversation] = useState(null);
  const [recentMessages, setRecentMessages] = useState([]);

  const candidateName =
    candidate?.full_name || [candidate?.first_name, candidate?.last_name].filter(Boolean).join(" ");

  useEffect(() => {
    if (!open) {
      setBody("");
      setExistingConversation(null);
      setRecentMessages([]);
      return;
    }
    if (!candidate?.id) return;

    setLoading(true);
    sh.get("/recruiter/conversations")
      .then((res) => {
        const conversation = (res.data?.data || []).find((c) => c.candidate?.id === candidate.id);
        setExistingConversation(conversation || null);
        if (!conversation) return null;

        return sh.get(`/recruiter/conversations/${conversation.id}`).then((showRes) => {
          setRecentMessages((showRes.data?.data?.messages || []).slice(-3));
        });
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [open, candidate?.id]);

  const conversationUrl = (conversationId) =>
    `/${i18nLocale}/recruiter?tab=messages&conversation=${conversationId}`;

  const handleSend = () => {
    const trimmed = body.trim();
    if (!trimmed || !candidate?.id || sending) return;

    setSending(true);
    sh.post("/recruiter/conversations", {
      authenticity_token: authenticityToken,
      candidate_id: candidate.id,
      body: trimmed,
    })
      .then((res) => {
        const conversationId = res.data?.data?.id;
        enqueueSnackbar(intl.formatMessage({ id: "recruiter.messaging.message_sent" }), {
          variant: "success",
          action: (snackbarKey) =>
            conversationId ? (
              <Button
                size="small"
                sx={{ color: "inherit", textTransform: "none", fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}
                onClick={() => {
                  closeSnackbar(snackbarKey);
                  window.location.href = conversationUrl(conversationId);
                }}
              >
                {intl.formatMessage({ id: "recruiter.messaging.open_conversation" })}
              </Button>
            ) : null,
        });
        onClose();
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar(intl.formatMessage({ id: "recruiter.messaging.error_sending" }), {
          variant: "error",
        });
      })
      .finally(() => setSending(false));
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={classes.modalContent}>
        <Box className={classes.header}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
            <Avatar
              src={candidate?.profile_picture_url || candidate?.profile_picture || undefined}
              alt={candidateName}
              sx={{ width: 40, height: 40 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} noWrap>
                {candidateName}
              </Typography>
              {candidate?.job_title && (
                <Typography variant="body2" color="textSecondary" noWrap>
                  {candidate.job_title}
                </Typography>
              )}
            </Box>
          </Box>
          <CloseIcon className={classes.closeBtn} onClick={onClose} />
        </Box>

        <Box className={classes.body}>
          {loading ? (
            <SquarehubLoader size={56} centered />
          ) : (
            <>
              {existingConversation && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2" sx={{ fontFamily: "Segoe UI Semibold, system-ui, sans-serif", color: "hsl(153, 68%, 25%)" }}>
                      <FormattedMessage id="recruiter.messaging.existing_conversation" />
                    </Typography>
                    <Typography
                      variant="body2"
                      onClick={() => {
                        window.location.href = conversationUrl(existingConversation.id);
                      }}
                      sx={{
                        color: "hsl(153, 68%, 25%)",
                        fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      <FormattedMessage id="recruiter.messaging.open_conversation" /> →
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    {recentMessages.map((message) => {
                      const fromRecruiter = message.sender_type === "Recruiter";
                      return (
                        <Box
                          key={message.id}
                          sx={{
                            alignSelf: fromRecruiter ? "flex-end" : "flex-start",
                            maxWidth: "85%",
                            px: 1.5,
                            py: 0.75,
                            bgcolor: fromRecruiter ? "#023331" : "hsl(160, 10%, 96%)",
                            color: fromRecruiter ? "#fff" : "#023331",
                            borderRadius: fromRecruiter ? "12px 12px 4px 12px" : "12px 12px 12px 4px",
                          }}
                        >
                          <Typography variant="body2" sx={{ color: "inherit", wordBreak: "break-word" }}>
                            {message.body}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={8}
                value={body}
                onChange={(event) => setBody(event.target.value)}
                placeholder={intl.formatMessage({ id: "recruiter.messaging.write_message" })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    "& fieldset": { borderColor: "hsl(160, 10%, 90%)" },
                    "&.Mui-focused fieldset": { borderColor: "hsl(153, 68%, 25%)" },
                  },
                }}
              />
            </>
          )}
        </Box>

        <Box className={classes.footer}>
          <Button className={classes.cancelBtn} onClick={onClose} disabled={sending}>
            <FormattedMessage id="cancel" />
          </Button>
          <Button
            className={classes.sendBtn}
            onClick={handleSend}
            disabled={!body.trim() || sending || loading}
          >
            {sending ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 24 }}>
                <SquarehubLoader size={22} />
              </Box>
            ) : (
              <FormattedMessage id="recruiter.messaging.send" />
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ContactCandidateModal;
